import React, { useEffect, useState, useRef } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { UploadCloud, Download, Plus, Search, X } from "lucide-react";

export default function InstitutionMembersPage() {
  const [members, setMembers] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [newMember, setNewMember] = useState({ name: "", email: "", role_within_institution: "student", student_type: "undergraduate" });
  const [credentialsShown, setCredentialsShown] = useState(null);
  const [uploadResult, setUploadResult] = useState(null);
  const fileRef = useRef(null);

  const load = async () => {
    setLoading(true);
    const r = await api.get("/institutions/members", { params: q ? { q } : {} });
    setMembers(r.data);
    setLoading(false);
  };
  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [q]);

  const downloadTemplate = async () => {
    const token = localStorage.getItem("kb_session_token");
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/institutions/members/template`;
    const r = await fetch(url, { credentials: "include", headers: token ? { Authorization: `Bearer ${token}` } : {} });
    const blob = await r.blob();
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = "institution_members_template.csv";
    link.click();
  };

  const addMember = async (e) => {
    e.preventDefault();
    try {
      const r = await api.post("/institutions/members", newMember);
      setCredentialsShown({ user: r.data.user, password: r.data.temp_password });
      setNewMember({ name: "", email: "", role_within_institution: "student", student_type: "undergraduate" });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const fd = new FormData(); fd.append("file", f);
    try {
      const token = localStorage.getItem("kb_session_token");
      const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/institutions/members/upload`, {
        method: "POST", body: fd, credentials: "include",
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      const j = await r.json();
      setUploadResult(j);
      toast.success(`Uploaded — ${j.created_count} created, ${j.skipped_count} skipped, ${j.errors_count} errors`);
      load();
    } catch (e) { toast.error("Upload failed"); }
    finally { if (fileRef.current) fileRef.current.value = ""; }
  };

  const downloadCredentialsCsv = () => {
    if (!uploadResult?.created?.length) return;
    const rows = [["email", "temp_password"], ...uploadResult.created.map((c) => [c.email, c.temp_password])];
    const csv = rows.map(r => r.map(v => `"${String(v).replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = "institution_temp_credentials.csv";
    link.click();
  };

  return (
    <div data-testid="institution-members-page">
      <div className="kb-mono text-[10px] uppercase tracking-[0.2em] text-kb-ink-muted">MEMBERS</div>
      <h1 className="font-serif text-3xl lg:text-4xl text-kb-ink mt-1">Manage your <em className="italic text-kb-primary">institution members</em></h1>

      <div className="mt-8 grid lg:grid-cols-12 gap-6">
        <form onSubmit={addMember} className="lg:col-span-5 kb-card p-6 space-y-3" data-testid="member-add-form">
          <div className="kb-mono text-[10px] uppercase tracking-[0.2em] text-kb-ink-muted">ADD SINGLE MEMBER</div>
          <input required className="kb-input" placeholder="Full name" value={newMember.name}
            onChange={(e) => setNewMember({ ...newMember, name: e.target.value })} data-testid="member-name-input" />
          <input required type="email" className="kb-input" placeholder="Email" value={newMember.email}
            onChange={(e) => setNewMember({ ...newMember, email: e.target.value })} data-testid="member-email-input" />
          <div className="grid grid-cols-2 gap-2">
            <select className="kb-input" value={newMember.role_within_institution}
              onChange={(e) => setNewMember({ ...newMember, role_within_institution: e.target.value })}
              data-testid="member-role-select">
              <option value="student">Student</option>
              <option value="faculty">Faculty</option>
            </select>
            <select className="kb-input" value={newMember.student_type}
              onChange={(e) => setNewMember({ ...newMember, student_type: e.target.value })}
              data-testid="member-stype-select">
              <option value="undergraduate">Undergraduate</option>
              <option value="graduate">Graduate</option>
            </select>
          </div>
          <button type="submit" className="kb-btn-primary w-full justify-center" data-testid="member-add-btn">
            <Plus className="w-4 h-4" /> Add member
          </button>
          <div className="kb-mono text-[10px] text-kb-ink-light">Member must reset password at first login.</div>
        </form>

        <div className="lg:col-span-7 space-y-4">
          <div className="kb-card p-6">
            <div className="kb-mono text-[10px] uppercase tracking-[0.2em] text-kb-ink-muted">BULK UPLOAD VIA CSV</div>
            <div className="mt-3 flex gap-2 flex-wrap">
              <button onClick={downloadTemplate} className="kb-btn-outline text-sm" data-testid="member-download-template">
                <Download className="w-3.5 h-3.5" /> Download template
              </button>
              <label className="kb-btn-primary text-sm cursor-pointer" data-testid="member-upload-label">
                <UploadCloud className="w-3.5 h-3.5" /> Upload CSV
                <input ref={fileRef} type="file" accept=".csv" onChange={onFile} className="hidden" data-testid="member-upload-input" />
              </label>
            </div>
            {uploadResult && (
              <div className="mt-4 text-xs space-y-3">
                <div className="grid grid-cols-3 gap-2">
                  <div className="bg-emerald-50 border border-emerald-200 rounded-sm p-2 text-emerald-700">Created: <span className="font-mono font-medium">{uploadResult.created_count}</span></div>
                  <div className="bg-amber-50 border border-amber-200 rounded-sm p-2 text-amber-700">Skipped: <span className="font-mono font-medium">{uploadResult.skipped_count}</span></div>
                  <div className="bg-rose-50 border border-rose-200 rounded-sm p-2 text-rose-700">Errors: <span className="font-mono font-medium">{uploadResult.errors_count}</span></div>
                </div>
                {uploadResult.created?.length > 0 && (
                  <div className="border border-slate-200 rounded-sm p-3 bg-slate-50">
                    <div className="flex items-center justify-between mb-2">
                      <div className="kb-mono text-[10px] uppercase tracking-wider text-kb-ink-muted">TEMP CREDENTIALS · share with members securely</div>
                      <button onClick={downloadCredentialsCsv} className="kb-mono text-[10px] uppercase tracking-wider text-kb-primary inline-flex items-center gap-1" data-testid="download-credentials-btn">
                        <Download className="w-3 h-3" /> Download CSV
                      </button>
                    </div>
                    <div className="space-y-1 max-h-44 overflow-auto" data-testid="bulk-credentials-list">
                      {uploadResult.created.map((c) => (
                        <div key={c.email} className="flex items-center justify-between text-[11px] font-mono py-1 border-b border-slate-200 last:border-0">
                          <span className="text-kb-ink truncate flex-1">{c.email}</span>
                          <span className="text-kb-primary ml-2">{c.temp_password}</span>
                        </div>
                      ))}
                    </div>
                    <div className="kb-mono text-[10px] text-kb-ink-light mt-2">All members must reset their password on first sign-in.</div>
                  </div>
                )}
                {uploadResult.skipped?.length > 0 && (
                  <details className="border border-slate-200 rounded-sm p-3 bg-amber-50/30">
                    <summary className="kb-mono text-[10px] uppercase tracking-wider text-amber-700 cursor-pointer">{uploadResult.skipped.length} skipped (already registered)</summary>
                    <div className="mt-2 space-y-0.5 max-h-24 overflow-auto font-mono text-[10px] text-kb-ink-muted">
                      {uploadResult.skipped.map((e) => <div key={e}>{e}</div>)}
                    </div>
                  </details>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {credentialsShown && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-6" data-testid="member-credentials-modal">
          <div className="bg-white p-6 rounded-sm max-w-md w-full">
            <div className="flex items-center justify-between">
              <h3 className="font-serif text-xl text-kb-ink">Member created — share credentials</h3>
              <button onClick={() => setCredentialsShown(null)}><X className="w-4 h-4" /></button>
            </div>
            <div className="mt-4 space-y-2 text-sm">
              <div>Email: <span className="font-mono text-kb-primary">{credentialsShown.user.email}</span></div>
              <div>Temp password: <span className="font-mono text-kb-primary">{credentialsShown.password}</span></div>
              <div className="kb-mono text-[10px] text-kb-ink-light">Member will be prompted to reset on first login.</div>
            </div>
          </div>
        </div>
      )}

      <div className="mt-8 relative">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-kb-ink-light" />
        <input className="kb-input pl-9" placeholder="Search members…" value={q}
          onChange={(e) => setQ(e.target.value)} data-testid="member-search-input" />
      </div>

      <div className="mt-4 border border-slate-200 rounded-sm bg-white overflow-hidden">
        <div className="grid grid-cols-12 px-4 lg:px-6 py-3 border-b border-slate-200 bg-slate-50 kb-mono text-[10px] uppercase tracking-wider text-kb-ink-muted">
          <div className="col-span-4">Member</div>
          <div className="col-span-2">Role</div>
          <div className="col-span-2">Type</div>
          <div className="col-span-2">Projects</div>
          <div className="col-span-2">Published</div>
        </div>
        {loading ? <div className="p-6 kb-mono text-xs text-kb-ink-muted">Loading…</div> :
          members.length === 0 ? <div className="p-10 text-center text-sm text-kb-ink-muted">No members yet.</div> :
          members.map((m) => (
            <div key={m.user_id} className="grid grid-cols-12 px-4 lg:px-6 py-3 border-b border-slate-200 last:border-0 items-center text-sm" data-testid={`member-row-${m.user_id}`}>
              <div className="col-span-12 lg:col-span-4">
                <div className="text-kb-ink font-medium">{m.name}</div>
                <div className="kb-mono text-[10px] text-kb-ink-light">{m.email}</div>
              </div>
              <div className="col-span-6 lg:col-span-2 kb-mono text-xs capitalize">{m.role}</div>
              <div className="col-span-6 lg:col-span-2 kb-mono text-xs capitalize">{m.student_type || "—"}</div>
              <div className="col-span-6 lg:col-span-2 font-mono text-kb-primary">{m.ideas_count}</div>
              <div className="col-span-6 lg:col-span-2 font-mono text-kb-primary">{m.ideas_published}</div>
            </div>
          ))}
      </div>
      <div className="kb-mono text-[10px] text-kb-ink-light mt-3">
        Note: institution view shows progress only — confidential research content stays with the researcher and mentor.
      </div>
    </div>
  );
}
